import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import productService from '../services/productService';
import { useDispatch } from 'react-redux';
import { updateProduct } from '../store/slices/productSlice';
import { resolveImageUrl, sanitizeImages } from '../utils/asset';
import './EditProduct.css';

const MAX_NEW_IMAGES = 5;

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // product local full object not needed outside initial load; omit storing entire product to reduce unused vars
  const [product, setProduct] = useState(null); // retained if needed later for diff logic
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [basePrice, setBasePrice] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [tiers, setTiers] = useState([]); // {minQty, unitPrice}
  const [available, setAvailable] = useState('');
  const [leadTimeDays, setLeadTimeDays] = useState('');
  const [moq, setMoq] = useState('');
  const [packSize, setPackSize] = useState('');
  const [modelNumber, setModelNumber] = useState('');
  const [sku, setSku] = useState('');
  const [categoriesText, setCategoriesText] = useState('');
  const [keywordsText, setKeywordsText] = useState('');
  const [incoterm, setIncoterm] = useState('FOB');
  const [originCountry, setOriginCountry] = useState('');
  const [originState, setOriginState] = useState('');
  const [shippingMethodsText, setShippingMethodsText] = useState('');
  const [attrList, setAttrList] = useState([]); // [{key:'', value:''}]
  const [isActive, setIsActive] = useState(false);
  const [existingImages, setExistingImages] = useState([]); // sanitized existing images
  // Track removed URLs only if backend needs to process deletions; currently unused so omitted to silence warning
  const [removedUrls, setRemovedUrls] = useState([]); // TODO: integrate with backend deletion if required
  const [newFiles, setNewFiles] = useState([]);
  const [newPreviews, setNewPreviews] = useState([]);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await productService.getProduct(id);
        if (!mounted) return;
        data.images = sanitizeImages(data.images);
        setProduct(data);
        setTitle(data.title || '');
        setDescription(data.description || '');
        setBasePrice(data.price?.base != null ? String(data.price.base) : '');
        setCurrency(data.price?.currency || 'USD');
        setTiers(Array.isArray(data.price?.tiers) ? data.price.tiers : []);
        setAvailable(data.inventory?.available != null ? String(data.inventory.available) : '');
        setLeadTimeDays(data.inventory?.leadTimeDays != null ? String(data.inventory.leadTimeDays) : '');
        setMoq(data.moq != null ? String(data.moq) : '');
        setPackSize(data.packSize != null ? String(data.packSize) : '');
        setModelNumber(data.modelNumber || '');
        setSku(data.sku || '');
        setCategoriesText(Array.isArray(data.categories) ? data.categories.join(', ') : '');
        setKeywordsText(Array.isArray(data.searchKeywords) ? data.searchKeywords.join(', ') : '');
        setIncoterm(data.shipping?.incoterm || 'FOB');
        setOriginCountry(data.shipping?.origin?.country || '');
        setOriginState(data.shipping?.origin?.state || '');
        setShippingMethodsText(Array.isArray(data.shipping?.methods) ? data.shipping.methods.join(', ') : '');
        // attributes map -> list
        if (data.attributes && typeof data.attributes === 'object') {
          setAttrList(Object.entries(data.attributes).map(([k,v]) => ({ key:k, value:String(v) })));
        }
        setIsActive(!!data.isActive);
        setExistingImages(data.images);
        setLoading(false);
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.message || err.message || 'Failed to load product');
        setLoading(false);
      }
    })();
    return () => { mounted = false; newPreviews.forEach(p => URL.revokeObjectURL(p.url)); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // intentionally exclude newPreviews to avoid re-fetch on preview state changes

  const onNewFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    const nextFiles = [...newFiles];
    const nextPrev = [...newPreviews];
    for (const file of selected) {
      if (nextFiles.length >= MAX_NEW_IMAGES) break;
      if (!['image/jpeg','image/png','image/webp'].includes(file.type)) { setLocalError('Unsupported image type'); continue; }
      if (file.size > 3*1024*1024) { setLocalError('Image exceeds 3MB'); continue; }
      nextFiles.push(file);
      nextPrev.push({ url: URL.createObjectURL(file), name: file.name });
    }
    setNewFiles(nextFiles);
    setNewPreviews(nextPrev);
    e.target.value = '';
  };

  const removeExisting = (url) => {
    setExistingImages(imgs => imgs.filter(i => i.url !== url));
    setRemovedUrls(list => list.includes(url) ? list : [...list, url]);
  };
  const removeNew = (idx) => {
    const next = [...newFiles];
    const nextPrev = [...newPreviews];
    const removed = nextPrev[idx];
    if (removed) URL.revokeObjectURL(removed.url);
    next.splice(idx,1); nextPrev.splice(idx,1);
    setNewFiles(next); setNewPreviews(nextPrev);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    if (!title.trim()) return setLocalError('Title required');
    if (basePrice && Number.isNaN(Number(basePrice))) return setLocalError('Base price invalid');
    for (const t of tiers) {
      if (!t.minQty || !t.unitPrice) return setLocalError('All tier rows need min qty & unit price');
    }
    for (const row of attrList) {
      if (row.key && !row.value) return setLocalError(`Attribute '${row.key}' missing value`);
    }
    const form = new FormData();
    form.append('title', title.trim());
    if (modelNumber.trim()) form.append('modelNumber', modelNumber.trim());
    if (sku.trim()) form.append('sku', sku.trim());
    if (description.trim()) form.append('description', description.trim());
    if (categoriesText.trim()) form.append('categories', categoriesText.trim());
    // Price object
    if (basePrice) {
      const priceObj = { base: Number(basePrice) };
      if (currency) priceObj.currency = currency;
      if (tiers.length) priceObj.tiers = tiers.map(t => ({ minQty: Number(t.minQty), unitPrice: Number(t.unitPrice) }));
      form.append('price', JSON.stringify(priceObj));
    }
    const inventoryObj = { available: available ? Number(available) : 0 };
    if (leadTimeDays) inventoryObj.leadTimeDays = Number(leadTimeDays);
    form.append('inventory', JSON.stringify(inventoryObj));
    if (moq) form.append('moq', Number(moq));
    if (packSize) form.append('packSize', Number(packSize));
    // Shipping
    const shippingObj = {};
    if (incoterm) shippingObj.incoterm = incoterm;
    if (originCountry || originState) shippingObj.origin = { country: originCountry || undefined, state: originState || undefined };
    if (shippingMethodsText.trim()) shippingObj.methods = shippingMethodsText.split(',').map(m => m.trim()).filter(Boolean);
    if (Object.keys(shippingObj).length) form.append('shipping', JSON.stringify(shippingObj));
    // Attributes
    const attrMap = {};
    attrList.filter(r => r.key.trim()).forEach(r => { attrMap[r.key.trim()] = r.value; });
    if (Object.keys(attrMap).length) form.append('attributes', JSON.stringify(attrMap));
    if (keywordsText.trim()) form.append('searchKeywords', keywordsText.trim());
    form.append('isActive', isActive ? 'true' : 'false');
    newFiles.forEach(f => form.append('images', f));
    if (newFiles.length) form.append('_hasImages', 'true');
    // Intentionally DO NOT send existingImages back as raw; backend persists unless replaced
    try {
      await dispatch(updateProduct({ id, productData: form })).unwrap();
      navigate(`/products/${id}`);
    } catch (err) {
      // slice handles error state; show local anyway
      setLocalError(err?.message || 'Update failed');
    }
  };

  const addTier = () => setTiers(ts => [...ts, { minQty:'', unitPrice:'' }]);
  const updateTier = (i, field, value) => setTiers(ts => ts.map((t,idx) => idx===i ? { ...t, [field]: value } : t));
  const removeTier = (i) => setTiers(ts => ts.filter((_,idx) => idx!==i));

  const addAttribute = () => setAttrList(list => [...list, { key:'', value:'' }]);
  const updateAttr = (i, field, value) => setAttrList(list => list.map((row,idx) => idx===i ? { ...row, [field]: value } : row));
  const removeAttr = (i) => setAttrList(list => list.filter((_,idx) => idx!==i));

  if (loading) return <div className="page-wrapper"><p>Loading...</p></div>;
  if (error) return <div className="page-wrapper"><p className="error-inline">{error}</p><button className="btn" onClick={() => navigate(-1)}>Back</button></div>;

  return (
    <div className="edit-product page-wrapper">
      <div className="page-header">
        <h1>Edit Product</h1>
        <button className="btn text" onClick={() => navigate(-1)}>Back</button>
      </div>
      <form className="panel product-form" onSubmit={onSubmit}>
        <div className="form-grid">
          <div className="field-group">
            <label>Title *</label>
            <input value={title} onChange={e => setTitle(e.target.value)} required />
          </div>
          <div className="field-group">
            <label>Model Number</label>
            <input value={modelNumber} onChange={e => setModelNumber(e.target.value)} />
          </div>
          <div className="field-group">
            <label>SKU</label>
            <input value={sku} onChange={e => setSku(e.target.value)} />
          </div>
          <div className="field-group">
            <label>Base Price</label>
            <input type="number" step="0.01" value={basePrice} onChange={e => setBasePrice(e.target.value)} />
          </div>
          <div className="field-group">
            <label>Currency</label>
            <input value={currency} onChange={e => setCurrency(e.target.value.toUpperCase())} maxLength={3} />
          </div>
          <div className="field-group">
            <label>Available</label>
            <input type="number" value={available} onChange={e => setAvailable(e.target.value)} />
          </div>
          <div className="field-group">
            <label>Lead Time (days)</label>
            <input type="number" value={leadTimeDays} onChange={e => setLeadTimeDays(e.target.value)} />
          </div>
          <div className="field-group">
            <label>MOQ</label>
            <input type="number" value={moq} onChange={e => setMoq(e.target.value)} />
          </div>
          <div className="field-group">
            <label>Pack Size</label>
            <input type="number" value={packSize} onChange={e => setPackSize(e.target.value)} />
          </div>
          <div className="field-group">
            <label>Categories (comma)</label>
            <input value={categoriesText} onChange={e => setCategoriesText(e.target.value)} />
          </div>
          <div className="field-group full">
            <label>Search Keywords (comma)</label>
            <input value={keywordsText} onChange={e => setKeywordsText(e.target.value)} />
          </div>
          <div className="field-group full">
            <label>Description</label>
            <textarea rows={5} value={description} onChange={e => setDescription(e.target.value)} />
          </div>
          {/* Tier Pricing */}
          <div className="field-group full">
            <label>Tier Pricing</label>
            {tiers.length === 0 && <p className="muted">No tiers added.</p>}
            {tiers.map((t,i) => (
              <div key={i} className="inline-row tier-row">
                <input type="number" placeholder="Min Qty" value={t.minQty} onChange={e => updateTier(i,'minQty', e.target.value)} />
                <input type="number" step="0.01" placeholder="Unit Price" value={t.unitPrice} onChange={e => updateTier(i,'unitPrice', e.target.value)} />
                <button type="button" className="btn text danger" onClick={() => removeTier(i)}>Remove</button>
              </div>
            ))}
            <button type="button" className="btn small" onClick={addTier}>Add Tier</button>
          </div>
          {/* Shipping */}
            <div className="field-group full">
              <label>Shipping</label>
              <div className="inline-row">
                <div>
                  <small>Incoterm</small>
                  <select value={incoterm} onChange={e => setIncoterm(e.target.value)}>
                    {['EXW','FOB','CIF','DDP','DDU'].map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div>
                  <small>Origin Country</small>
                  <input value={originCountry} onChange={e => setOriginCountry(e.target.value)} />
                </div>
                <div>
                  <small>Origin State</small>
                  <input value={originState} onChange={e => setOriginState(e.target.value)} />
                </div>
              </div>
              <div className="inline-row">
                <div style={{flex:1}}>
                  <small>Methods (comma)</small>
                  <input value={shippingMethodsText} onChange={e => setShippingMethodsText(e.target.value)} />
                </div>
              </div>
            </div>
          {/* Attributes */}
          <div className="field-group full">
            <label>Attributes</label>
            {attrList.length === 0 && <p className="muted">No attributes added.</p>}
            {attrList.map((row,i) => (
              <div key={i} className="inline-row attr-row">
                <input placeholder="Key" value={row.key} onChange={e => updateAttr(i,'key', e.target.value)} />
                <input placeholder="Value" value={row.value} onChange={e => updateAttr(i,'value', e.target.value)} />
                <button type="button" className="btn text danger" onClick={() => removeAttr(i)}>Remove</button>
              </div>
            ))}
            <button type="button" className="btn small" onClick={addAttribute}>Add Attribute</button>
          </div>
          {/* Activation */}
          <div className="field-group">
            <label>Active</label>
            <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
          </div>
          <div className="field-group full">
            <label>Existing Images</label>
            {existingImages.length ? (
              <div className="image-previews">
                {existingImages.map(img => (
                  <figure key={img.url}>
                    <img src={resolveImageUrl(img.url)} alt={img.alt || 'Product'} />
                    <button type="button" className="remove" onClick={() => removeExisting(img.url)}>×</button>
                  </figure>
                ))}
              </div>
            ) : <p className="muted">None</p>}
          </div>
          <div className="field-group full">
            <label>Add Images (up to {MAX_NEW_IMAGES})</label>
            <input type="file" multiple accept="image/jpeg,image/png,image/webp" onChange={onNewFiles} />
            {newPreviews.length > 0 && (
              <div className="image-previews">
                {newPreviews.map((p, idx) => (
                  <figure key={p.url}>
                    <img src={p.url} alt={p.name} />
                    <button type="button" className="remove" onClick={() => removeNew(idx)}>×</button>
                  </figure>
                ))}
              </div>
            )}
          </div>
        </div>
        {(localError) && <div className="error-inline">{localError}</div>}
        <div className="form-actions">
          <button type="button" className="btn text" onClick={() => navigate(-1)}>Cancel</button>
          <button type="submit" className="btn primary">Save Changes</button>
        </div>
      </form>
    </div>
  );
};

export default EditProduct;
